import React from 'react';
import { Check, Share2, Instagram, Linkedin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useAnalytics } from '@/hooks/useAnalytics';
import { getReferralShareMessage } from '@/utils/referralUtils';

interface WaitlistSuccessModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isAlreadyOnWaitlist?: boolean;
  referralCode?: string;
  referralUrl?: string;
}

const WaitlistSuccessModal: React.FC<WaitlistSuccessModalProps> = ({
  open,
  onOpenChange,
  isAlreadyOnWaitlist = false,
  referralCode,
  referralUrl
}) => {
  const { toast } = useToast();
  const { trackSocialShare, trackButtonClick } = useAnalytics();

  const handleShare = () => {
    const shareText = getReferralShareMessage(!!referralCode);
    const shareUrl = referralUrl || window.location.origin;

    trackButtonClick('Share Waitlist', 'Success Modal');

    if (navigator.share) {
      navigator.share({
        title: 'Join the Wabisabi Waitlist',
        text: shareText,
        url: shareUrl,
      }).then(() => {
        trackSocialShare('native_share', 'waitlist_modal');
      }).catch(console.error);
    } else {
      navigator.clipboard.writeText(`${shareText} ${shareUrl}`).then(() => {
        trackSocialShare('clipboard', 'waitlist_modal');
        toast({
          title: "Link copied!",
          description: "Share link has been copied to your clipboard.",
        });
      }).catch(() => {
        const encodedText = encodeURIComponent(shareText);
        const encodedUrl = encodeURIComponent(shareUrl);
        trackSocialShare('twitter_fallback', 'waitlist_modal');
        window.open(`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`, '_blank');
      });
    }
  };

  const handleSocialShare = (platform: string) => {
    const shareText = getReferralShareMessage(!!referralCode);
    const shareUrl = referralUrl || window.location.origin;
    const encodedText = encodeURIComponent(shareText);
    const encodedUrl = encodeURIComponent(shareUrl);

    trackSocialShare(platform, 'waitlist_modal');

    switch (platform) {
      case 'instagram':
        navigator.clipboard.writeText(`${shareText} ${shareUrl}`).then(() => {
          toast({
            title: "Link copied!",
            description: "Share this on Instagram! Link copied to clipboard.",
          });
        });
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, '_blank');
        break;
      default:
        handleShare();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white">
        <DialogHeader>
          <div className="flex flex-col items-center text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center bg-[#6C4F77]/10">
              <Check className="w-8 h-8 text-[#6C4F77]" />
            </div>
            <DialogTitle className="text-2xl font-semibold text-[#6C4F77]">
              {isAlreadyOnWaitlist ? "You're already on the waitlist! ✨" : "You're on the waitlist! 🎉"}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4 pt-4 overflow-x-hidden">
          <div className="space-y-3 text-center">
            {isAlreadyOnWaitlist ? (
              <>
                <span className="text-sm leading-relaxed text-[#2C2C2C]">
                  Good news! You're already part of our community that believes relationships are worth remembering.
                </span>
                <br />
                <span className="text-sm leading-relaxed text-[#2C2C2C]">
                  No need to sign up again - you'll be among the first to know when Wabisabi is ready!
                </span>
                <br />
                <span className="text-sm font-medium text-[#6C4F77]">
                  While you wait, help us spread the word about building deeper connections!
                </span>
              </>
            ) : (
              <>
                <span className="text-sm leading-relaxed text-[#2C2C2C]">
                  Thanks for joining the Wabisabi waitlist! You're now part of a community that believes relationships are worth remembering.
                </span>
                <br />
                <span className="text-sm leading-relaxed text-[#2C2C2C]">
                  We're working hard to bring you something special. You'll be among the first to know when Wabisabi is ready.
                </span>
                <br />
                <span className="text-sm font-medium text-[#6C4F77]">
                  Help us spread the word about building deeper connections!
                </span>
              </>
            )}
          </div>

          {referralCode && (
            <div className="bg-[#F8F5F2] rounded-lg p-4 space-y-2 overflow-hidden">
              <p className="text-sm font-semibold text-[#6C4F77]">Your Referral Link:</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="overflow-x-auto">
                    <code className="text-xs bg-white px-3 py-2 rounded-md inline-block whitespace-nowrap font-mono">
                      {referralUrl}
                    </code>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-shrink-0"
                  onClick={() => {
                    navigator.clipboard.writeText(referralUrl || '');
                    toast({
                      title: "Copied!",
                      description: "Referral link copied to clipboard",
                    });
                  }}
                >
                  Copy
                </Button>
              </div>
              <p className="text-xs text-[#999999]">
                Share this link with friends to move up the waitlist!
              </p>
            </div>
          )}

          <div className="space-y-3">
            <Button
              onClick={handleShare}
              className="w-full font-semibold bg-[#6C4F77] text-white hover:bg-[#5A3A68]"
            >
              <Share2 className="mr-2 h-4 w-4" />
              Invite someone special
            </Button>

            <div className="flex items-center justify-center gap-4 py-2">
              <button
                onClick={() => handleSocialShare('linkedin')}
                className="w-10 h-10 rounded-full bg-[#6C4F77]/10 hover:bg-[#6C4F77]/20 flex items-center justify-center transition-all duration-300 hover:scale-110 group"
                aria-label="Share on LinkedIn"
              >
                <Linkedin className="w-5 h-5 text-[#6C4F77] group-hover:text-[#F4978E] transition-colors" />
              </button>
              <button
                onClick={() => handleSocialShare('instagram')}
                className="w-10 h-10 rounded-full bg-[#6C4F77]/10 hover:bg-[#6C4F77]/20 flex items-center justify-center transition-all duration-300 hover:scale-110 group"
                aria-label="Share on Instagram"
              >
                <Instagram className="w-5 h-5 text-[#6C4F77] group-hover:text-[#F4978E] transition-colors" />
              </button>
            </div>
          </div>

          <p className="text-xs text-center text-[#2C2C2C]/60 pt-2">
            You'll be the first to know when Wabisabi launches. We promise to only send you updates that matter.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WaitlistSuccessModal;
